import { type ProcessUploadResponse, useMutation, useUploadEvidence } from 'api';
import { addError, logger } from 'monitor';
import { useCallback } from 'react';

import { buildEvidenceMetadataFromExif, extractExifData } from '../../../evidence';
import type { Bale, NewEvidence } from '../../types';
import { formatEvidenceRejectionReason } from '../../utils/formatEvidenceRejectionReason';
import { getAnalysisStatusAndRejectionReason } from '../../utils/getAnalysisStatusAndRejectionReason/getAnalysisStatusAndRejectionReason';
import { trackBaleSopOutcome, trackEvidenceSopOutcome } from '../../utils/trackBaleSopOutcome';
import { useAnalysisStatus } from '../useAnalysisStatus/useAnalysisStatus';
import { useBaleAnalysisStore, useEvidenceAnalysisStore } from '../useBaleAnalysis/useBaleAnalysisStore';

/**
 * @deprecated
 */
export const useUploadBale = () => {
  const {
    mode,
    parentId,
    actions: { addBale, updateBale },
  } = useBaleAnalysisStore();
  const { mutateAsync: uploadEvidence } = useUploadEvidence(mode);

  const uploadBale = useCallback(
    async ({ bale, model, startTime }: { bale: Bale; model?: string; startTime: number }) => {
      const exifData = await extractExifData(bale.photo);
      return uploadEvidence({
        metadata: buildEvidenceMetadataFromExif(exifData, undefined),
        model,
        onProgress: (progress) => {
          updateBale({ ...bale, uploadProgress: progress });
        },
        parentId,
        photo: bale.photo,
        startTime,
      });
    },
    [uploadEvidence, updateBale, parentId]
  );

  return useMutation<ProcessUploadResponse, Error, { bale: Bale; model?: string; startTime: number }>({
    mutationFn: uploadBale,
    onError: (error, { bale }) => {
      const analysisStatus =
        error?.message === 'invalid file format' ? ('rejected-photo' as const) : ('retry' as const);

      const isPhotoRejected = analysisStatus === 'rejected-photo';
      const formattedReason = isPhotoRejected ? formatEvidenceRejectionReason(error?.message) : undefined;

      const failedBale = {
        ...bale,
        analysisStatus,
        photoRejectedReason: formattedReason,
      };

      if (isPhotoRejected) {
        trackBaleSopOutcome(failedBale, 'model');
      } else {
        addError(error);
      }

      updateBale({
        ...failedBale,
        isLoading: false,
      });
    },
    onMutate: ({ bale }) => {
      addBale({ ...bale, isLoading: true });
    },
    onSuccess: (analysis, { bale }) => {
      logger.debug('Bale analysis', {
        analysis,
        bale,
      });
      const analysisResult = analysis.model_results
        ? getAnalysisStatusAndRejectionReason(mode, analysis.model_results)
        : { analysisStatus: 'accepted' as const, photoRejectedReason: undefined, photoWarningReason: undefined };

      const analyzedBale = {
        ...bale,
        ...analysisResult,
        analysis,
        evidenceId: analysis.evidence_id,
        isLoading: false,
      };

      trackBaleSopOutcome(analyzedBale, 'model');
      updateBale(analyzedBale);

      return analysis;
    },
  });
};

export const useUploadMaterialEvidence = () => {
  const {
    mode,
    parentId,
    actions: { addEvidence: addBale, updateEvidence: updateBale },
  } = useEvidenceAnalysisStore();
  const { mutateAsync: uploadEvidence } = useUploadEvidence(mode);
  const { getStatusAndRejectionReason } = useAnalysisStatus();

  const upload = useCallback(
    async ({ evidence, model, startTime }: { evidence: NewEvidence; model?: string; startTime: number }) => {
      const exifData = await extractExifData(evidence.file);
      return uploadEvidence({
        metadata: buildEvidenceMetadataFromExif(exifData, undefined),
        model,
        onProgress: (progress) => {
          updateBale({ ...evidence, uploadProgress: progress });
        },
        parentId,
        photo: evidence.file,
        startTime,
      });
    },
    [uploadEvidence, updateBale, parentId]
  );

  return useMutation<ProcessUploadResponse, Error, { evidence: NewEvidence; model?: string; startTime: number }>({
    mutationFn: upload,
    onError: (error, { evidence: bale }) => {
      const analysisStatus =
        error?.message === 'invalid file format' ? ('rejected-photo' as const) : ('retry' as const);

      const isPhotoRejected = analysisStatus === 'rejected-photo';
      const formattedReason = isPhotoRejected ? formatEvidenceRejectionReason(error?.message) : undefined;

      const failedBale = {
        ...bale,
        analysisStatus,
        photoRejectedReason: formattedReason,
      };

      if (isPhotoRejected) {
        trackEvidenceSopOutcome(failedBale, 'model');
      } else {
        addError(error);
      }

      updateBale({
        ...failedBale,
        isLoading: false,
      });
    },
    onMutate: ({ evidence: bale }) => {
      addBale({ ...bale, isLoading: true });
    },
    onSuccess: (analysis, { evidence: bale }) => {
      logger.debug('Bale analysis', {
        analysis,
        bale,
      });
      const analysisResult = getStatusAndRejectionReason(mode, analysis.model_results);

      if (!analysis.evidence_id) {
        addError(Error('No evidence ID found'));
        return;
      }

      const analyzedBale = {
        ...bale,
        ...analysisResult,
        analysis,
        evidenceId: analysis.evidence_id,
        isLoading: false,
        photo: analysis.image.url,
        variant: 'online' as const,
      };

      trackEvidenceSopOutcome(analyzedBale, 'model');
      updateBale(analyzedBale);

      return analysis;
    },
  });
};
